import {
  getStoreyIndex,
  getStoreysBelow,
} from '@shared/helpers/design/storey.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import type { Storey } from '@shared/models/design/storey.model';
import type { ThreeJsMode } from './types';

/**
 * Index of the selected storey within {@link storeys}, or undefined when the
 * id is missing or not in this building.
 */
export const getSelectedStoreyRenderIndex = (
  storeys: Storey[],
  selectedStoreyId: Storey['id'] | undefined,
): number | undefined => {
  if (!selectedStoreyId) return undefined;
  const index = getStoreyIndex(storeys, selectedStoreyId);
  return index >= 0 ? index : undefined;
};

/**
 * Floor at {@link selectedIndex}, or the top floor when this stack is shorter.
 * Missing or negative {@link selectedIndex} means there is no floor to draw.
 */
const resolveStoreyAtSelectedIndex = (
  storeys: Storey[],
  selectedIndex: number | undefined,
): Storey | undefined => {
  if (selectedIndex === undefined || selectedIndex < 0) return undefined;
  return storeys[selectedIndex] ?? storeys.at(-1);
};

/**
 * Storeys to draw for one building in the current 3D view mode.
 * Single-storey mode shows the floor at {@link selectedIndex}. Selected-and-below
 * includes that floor and every storey under it. A shorter sibling clamps to
 * its top floor so it stays visible (DEV-2576). Missing or negative
 * {@link selectedIndex} draws nothing in those modes.
 */
export const resolveStoreysForThreeJsMode = (
  storeys: Storey[],
  mode: ThreeJsMode,
  selectedIndex: number | undefined,
): Storey[] => {
  switch (mode) {
    case 'all-storeys':
      return storeys;
    case 'selected-storey': {
      const storey = resolveStoreyAtSelectedIndex(storeys, selectedIndex);
      return storey ? [storey] : [];
    }
    case 'selected-and-below': {
      const storey = resolveStoreyAtSelectedIndex(storeys, selectedIndex);
      if (!storey) return [];
      return [...getStoreysBelow(storeys, storey), storey];
    }
    default:
      return assertUnreachable(mode);
  }
};
